import ProtectedRouter from "@/context/auth/ProtectedRouter";
import { TenantProvider } from "@/context/TenantContext";
import { useTenant } from "@/context/useTenant";
import MainLayout from "@/layouts/MainLayout";
import StarterLayout from "@/layouts/StarterLayout";
import NotFoundPage from "@/pages/NotFoundPage";
import routes from "@/utils/routes";
import { BrowserRouter, Navigate, Outlet, Route, Routes } from "react-router-dom";
import { ATSProviderWrapper } from "@/Project/src/components/ATSProviderWrapper";

function App() {
  return (
    <TenantProvider>
      <AppWithRouter />
    </TenantProvider>
  );
}

const AppWithRouter = () => {
  const { tenantId } = useTenant();
  
  return (
    <BrowserRouter>
      <ConditionalTenantCheck tenantId={tenantId} />
    </BrowserRouter>
  );
};

const ConditionalTenantCheck = ({ tenantId }: { tenantId: string | null }) => {
  const publicRoutes = routes.find(m => m.type === "public")?.routes ?? [];
  const authRoutes = routes.find(m => m.type === "auth")?.routes ?? [];
  const atsRoutes = routes.find(m => m.type === "ats")?.routes ?? [];

  // Check if current path is a candidate/ATS route (no tenant required)
  const isCandidateRoute = window.location.pathname.startsWith('/candidate') || 
                          window.location.pathname.startsWith('/ats') ||
                          window.location.pathname === '/';

  // If no tenant ID and NOT a candidate route, show tenant selection page
  if (!tenantId && !isCandidateRoute) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <h1 className="mb-4 text-2xl font-bold">Tenant ID Required</h1>
          <p className="text-gray-600">
            Please provide a tenant ID in the URL path (e.g.,
            /your-tenant-id/login)
          </p>
        </div>
      </div>
    );
  }

  return (
    <Routes>
        <Route element={<TopLevelComponent />}>
          {/* Root redirect - always go to candidate login first */}
          <Route index element={<Navigate to="/candidate/login" replace />} />
          
          {publicRoutes.map(({ path, element: Page }) => (
            <Route key={path} path={path} element={<StarterLayout />}>
              <Route index element={<Page />} />
            </Route>
          ))}

          {/* ATS (Isolated) Routes */}
          <Route element={<ATSProviderWrapper />}>
            {(atsRoutes as any[])
              .filter(r => !r.protected)
              .map(({ path, element: Page }: any) => (
                <Route key={path} path={path} element={<Page />} />
              ))}
          </Route>

          {/* Protected routes */}
          <Route element={<ProtectedRouter />}>
            <Route element={<MainLayout />}>
              {authRoutes.map(({ path, element: Page }) => (
                <Route key={path} path={path} element={<Page />} />
              ))}
              <Route path="*" element={<NotFoundPage />} />
            </Route>

            {/* ATS Protected Routes - WITHOUT MainLayout */}
            {(atsRoutes as any[])
              .filter(r => r.protected)
              .map(({ path, element: Page }: any) => (
                <Route key={path} path={path} element={<Page />} />
              ))}
          </Route>
        </Route>
      </Routes>
  );
};

const TopLevelComponent = () => {
  return <Outlet />;
};

export default App;
